import React, { useContext, useState } from 'react';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';

type FormWithStateContextProviderProps<
  FormValues extends FieldValues,
  StateType,
> = {
  children: React.ReactNode;
  formMethods: UseFormReturn<FormValues>;
  initialState: StateType;
};

type FormWithStateContextType<FormValues extends FieldValues, StateType> = {
  state: StateType;
  setState: (state: StateType) => void;
  formMethods: UseFormReturn<FormValues>;
};

const FormWithStateContext = React.createContext<FormWithStateContextType<
  any,
  any
> | null>(null);

const FormWithStateContextProvider = <
  FormValues extends FieldValues,
  StateType,
>({
  children,
  formMethods,
  initialState,
}: FormWithStateContextProviderProps<FormValues, StateType>) => {
  const [state, setState] = useState<StateType>(initialState);

  const contextValue = {
    state,
    setState,
    formMethods,
  };

  return (
    <FormWithStateContext.Provider value={contextValue}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </FormWithStateContext.Provider>
  );
};

export const useFormWithStateContext = <
  FormValues extends FieldValues,
  StateType = never,
>() =>
  useContext(FormWithStateContext) as FormWithStateContextType<
    FormValues,
    StateType
  >;

export default FormWithStateContextProvider;
